// =============================================================================
// PEDRA — src/optimizer/packer.ts
// Preparacao dos dados e agregacao dos resultados: expansao de quantidades,
// fita de borda, agrupamento por material, pilha de chapas identicas e
// estatisticas finais.
// =============================================================================

import type {
  EdgeBandType,
  Id,
  Micron,
  Micron2,
  OptimizationStats,
  Part,
  Settings,
  SheetLayout,
  StackMode,
  StockSheet,
  UnfitPart,
  UnfitReason,
} from '../core/types';
import type { PackItem } from './guillotine';
import { layoutHash } from './cuts';
import { formatDimensions } from '../core/units';

// -----------------------------------------------------------------------------
// FITA DE BORDA
// -----------------------------------------------------------------------------

/** Espessura por id de fita. Ids desconhecidos contam como zero. */
export function bandThicknessMap(bands?: readonly EdgeBandType[]): Map<Id, Micron> {
  const map = new Map<Id, Micron>();
  if (!bands) return map;
  for (const b of bands) {
    const t = Number.isFinite(b.thickness) && b.thickness > 0 ? b.thickness : 0;
    map.set(b.id, t);
  }
  return map;
}

function thicknessOf(map: Map<Id, Micron>, id: Id | null): Micron {
  if (id === null) return 0;
  return map.get(id) ?? 0;
}

// -----------------------------------------------------------------------------
// PREPARACAO DE UMA PECA
// -----------------------------------------------------------------------------

export interface PreparedPart {
  part: Part;
  /** Copias efetivamente pedidas (inteiro >= 0). */
  qty: number;
  /** Dimensoes DE CORTE (informada menos a fita dos lados correspondentes). */
  cutWidth: Micron;
  cutLength: Micron;
  invalid: boolean;
  /** Motivo em pt-BR quando `invalid`. */
  message: string;
}

/**
 * Normaliza uma peca para o otimizador.
 *
 * Fita de borda (`edgeBandingEnabled`): a dimensao de CORTE e a informada
 * MENOS a espessura das fitas dos lados correspondentes
 * (`width -= left + right`, `length -= top + bottom`). Se sobrar zero ou
 * menos, a peca e invalida.
 */
export function preparePart(
  part: Part,
  settings: Settings,
  bands: Map<Id, Micron>,
): PreparedPart {
  const rawQty = part.qty;
  const qty = Number.isFinite(rawQty) && rawQty > 0 ? Math.floor(rawQty) : 0;

  const dims = formatDimensions(
    part.length,
    part.width,
    settings.unit,
    settings.decimalPlaces,
    settings.dimensionOrder,
  );
  const name = part.label.trim() !== '' ? `"${part.label.trim()}"` : 'sem nome';

  if (
    !Number.isFinite(part.width) ||
    !Number.isFinite(part.length) ||
    part.width <= 0 ||
    part.length <= 0
  ) {
    return {
      part,
      qty,
      cutWidth: 0,
      cutLength: 0,
      invalid: true,
      message: `A peça ${name} está sem medidas válidas (${dims}). Informe comprimento e largura maiores que zero.`,
    };
  }

  let cutWidth = part.width;
  let cutLength = part.length;

  if (settings.edgeBandingEnabled) {
    const b = part.edgeBanding;
    const side = thicknessOf(bands, b.left) + thicknessOf(bands, b.right);
    const ends = thicknessOf(bands, b.top) + thicknessOf(bands, b.bottom);
    cutWidth = part.width - side;
    cutLength = part.length - ends;
    if (cutWidth <= 0 || cutLength <= 0) {
      return {
        part,
        qty,
        cutWidth: 0,
        cutLength: 0,
        invalid: true,
        message: `A fita de borda da peça ${name} (${dims}) é maior que a própria peça. Reduza a espessura da fita ou aumente a peça.`,
      };
    }
  }

  return { part, qty, cutWidth, cutLength, invalid: false, message: '' };
}

// -----------------------------------------------------------------------------
// EXPANSAO DE QUANTIDADES
// -----------------------------------------------------------------------------

/**
 * Uma `PackItem` por copia fisica. Pecas desativadas, com quantidade zero ou
 * invalidas nao entram — o tratamento delas (mensagem de `UnfitPart`) fica a
 * cargo de `optimize()`, que usa `preparePart` diretamente.
 */
export function expandParts(
  parts: readonly Part[],
  settings: Settings,
  edgeBands?: readonly EdgeBandType[],
): PackItem[] {
  const bands = bandThicknessMap(edgeBands);
  const out: PackItem[] = [];
  for (const part of parts) {
    if (!part.enabled) continue;
    const prep = preparePart(part, settings, bands);
    if (prep.invalid || prep.qty <= 0) continue;
    for (let i = 0; i < prep.qty; i++) {
      out.push({
        partId: part.id,
        instance: i,
        label: part.label,
        color: part.color,
        material: part.material,
        width: prep.cutWidth,
        length: prep.cutLength,
        orientation: part.orientation,
        edgeBanding: part.edgeBanding,
      });
    }
  }
  return out;
}

// -----------------------------------------------------------------------------
// AGRUPAMENTO POR MATERIAL
// -----------------------------------------------------------------------------

/**
 * Segrega por material (comparacao por `trim()` exato).
 * Com `considerMaterial === false` tudo cai num unico grupo `'*'`.
 */
export function groupByMaterial<T extends { material: string }>(
  items: readonly T[],
  considerMaterial: boolean,
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = considerMaterial ? item.material.trim() : '*';
    const arr = map.get(key);
    if (arr === undefined) map.set(key, [item]);
    else arr.push(item);
  }
  return map;
}

// -----------------------------------------------------------------------------
// PILHA DE CHAPAS IDENTICAS
// -----------------------------------------------------------------------------

/**
 * Junta chapas com plano de corte identico num unico diagrama com `repeat > 1`.
 *  - `'never'`  nunca agrupa (`repeat` sempre 1);
 *  - `'always'` agrupa qualquer conjunto identico;
 *  - `'auto'`   agrupa a partir de 3 chapas iguais.
 * A ordem de saida segue a primeira ocorrencia de cada plano.
 */
export function stackIdenticalSheets(sheets: SheetLayout[], mode: StackMode): SheetLayout[] {
  if (sheets.length === 0) return [];
  if (mode === 'never') {
    return sheets.map((s) => ({ ...s, repeat: 1 }));
  }

  const threshold = mode === 'always' ? 1 : 3;
  const groups = new Map<string, SheetLayout[]>();
  const order: string[] = [];

  for (const s of sheets) {
    const key = `${s.stockSheetId}|${s.width}x${s.length}|${layoutHash(s.placements)}`;
    const arr = groups.get(key);
    if (arr === undefined) {
      groups.set(key, [s]);
      order.push(key);
    } else {
      arr.push(s);
    }
  }

  const out: SheetLayout[] = [];
  for (const key of order) {
    const group = groups.get(key) as SheetLayout[];
    if (group.length >= threshold) {
      let repeat = 0;
      for (const s of group) repeat += s.repeat > 0 ? s.repeat : 1;
      out.push({ ...(group[0] as SheetLayout), repeat });
    } else {
      for (const s of group) out.push({ ...s, repeat: s.repeat > 0 ? s.repeat : 1 });
    }
  }
  return out;
}

// -----------------------------------------------------------------------------
// ESTATISTICAS
// -----------------------------------------------------------------------------

/**
 * Consolida os numeros do relatorio. Todas as somas multiplicam por `repeat`,
 * porque uma chapa agrupada representa varias chapas FISICAS.
 * Os metros de fita usam as dimensoes INFORMADAS (acabadas), nunca as de corte.
 */
export function computeStats(
  sheets: readonly SheetLayout[],
  unfit: readonly UnfitPart[],
  parts: readonly Part[],
  edgeBands: readonly EdgeBandType[],
  settings: Settings,
  elapsedMs: number,
): OptimizationStats {
  let numSheets = 0;
  let totalSheetArea: Micron2 = 0;
  let totalUsedArea: Micron2 = 0;
  let totalCutLength: Micron = 0;
  let numCuts = 0;
  let numPlacedParts = 0;

  for (const sheet of sheets) {
    const repeat = sheet.repeat > 0 ? sheet.repeat : 1;
    numSheets += repeat;
    totalSheetArea += sheet.width * sheet.length * repeat;
    totalUsedArea += sheet.usedArea * repeat;
    totalCutLength += sheet.cutLength * repeat;
    numCuts += sheet.numCuts * repeat;
    numPlacedParts += sheet.placements.length * repeat;
  }

  let numUnfitParts = 0;
  for (const u of unfit) numUnfitParts += u.qty > 0 ? u.qty : 0;

  const edgeBandingTotals: Record<Id, Micron> = {};
  if (settings.edgeBandingEnabled && edgeBands.length > 0) {
    const known = new Set<Id>();
    for (const b of edgeBands) {
      edgeBandingTotals[b.id] = 0;
      known.add(b.id);
    }
    const byId = new Map<Id, Part>();
    for (const p of parts) byId.set(p.id, p);

    for (const sheet of sheets) {
      const repeat = sheet.repeat > 0 ? sheet.repeat : 1;
      for (const pl of sheet.placements) {
        const part = byId.get(pl.partId);
        if (part === undefined) continue;
        const b = part.edgeBanding;
        // top/bottom correm ao longo da LARGURA; left/right ao longo do COMPRIMENTO.
        addBand(edgeBandingTotals, known, b.top, part.width * repeat);
        addBand(edgeBandingTotals, known, b.bottom, part.width * repeat);
        addBand(edgeBandingTotals, known, b.left, part.length * repeat);
        addBand(edgeBandingTotals, known, b.right, part.length * repeat);
      }
    }
  }

  const totalWasteArea = totalSheetArea - totalUsedArea;
  const usedAreaRatio = totalSheetArea > 0 ? totalUsedArea / totalSheetArea : 0;

  return {
    numSheets,
    totalSheetArea,
    totalUsedArea,
    totalWasteArea,
    usedAreaRatio,
    wasteRatio: totalSheetArea > 0 ? totalWasteArea / totalSheetArea : 0,
    totalCutLength,
    numCuts,
    numPlacedParts,
    numUnfitParts,
    edgeBandingTotals,
    elapsedMs,
  };
}

function addBand(
  totals: Record<Id, Micron>,
  known: Set<Id>,
  id: Id | null,
  amount: Micron,
): void {
  if (id === null || !known.has(id)) return;
  totals[id] = (totals[id] ?? 0) + amount;
}

// -----------------------------------------------------------------------------
// ESTOQUE
// -----------------------------------------------------------------------------

/** Uma linha de estoque com o saldo mutavel usado durante uma tentativa. */
export interface StockEntry {
  sheet: StockSheet;
  /** Ordem original na tabela — desempate deterministico. */
  order: number;
  /** Quantas chapas fisicas esta linha ainda pode fornecer. */
  remaining: number;
  /** Saldo inicial, para restaurar entre tentativas. */
  initial: number;
  area: Micron2;
}

/** Teto de chapas de uma unica linha com estoque ilimitado. */
export const UNLIMITED_STOCK = 100_000;

/**
 * Monta o estoque utilizavel. `qty === null` significa ILIMITADO;
 * `useSingleStockUnit` limita cada linha a UMA chapa fisica.
 *
 * ATENCAO: aqui o limite e POR LINHA — com varias linhas de estoque o pool
 * ainda oferece uma chapa de cada, para que a busca possa ESCOLHER qual das
 * chapas usar. O teto de UMA unidade fisica NO TOTAL e aplicado em
 * `optimize()`, via orcamento global de chapas.
 */
export function buildStockPool(stock: readonly StockSheet[], settings: Settings): StockEntry[] {
  const out: StockEntry[] = [];
  stock.forEach((sheet, order) => {
    if (!sheet.enabled) return;
    if (!Number.isFinite(sheet.width) || !Number.isFinite(sheet.length)) return;
    if (sheet.width <= 0 || sheet.length <= 0) return;

    let qty: number;
    if (sheet.qty === null) qty = UNLIMITED_STOCK;
    else if (!Number.isFinite(sheet.qty) || sheet.qty <= 0) return;
    else qty = Math.floor(sheet.qty);

    if (settings.useSingleStockUnit) qty = Math.min(qty, 1);
    if (qty <= 0) return;

    out.push({
      sheet,
      order,
      remaining: qty,
      initial: qty,
      area: sheet.width * sheet.length,
    });
  });
  return out;
}

/** Restaura o saldo de todas as linhas (entre tentativas da metaheuristica). */
export function resetStockPool(pool: readonly StockEntry[]): void {
  for (const e of pool) e.remaining = e.initial;
}

// -----------------------------------------------------------------------------
// PECAS QUE NAO COUBERAM
// -----------------------------------------------------------------------------

/** Monta um `UnfitPart` com a mensagem pt-BR ja pronta para exibir. */
export function makeUnfit(
  part: Part,
  qty: number,
  reason: UnfitReason,
  settings: Settings,
  customMessage?: string,
): UnfitPart {
  const name = part.label.trim() !== '' ? `"${part.label.trim()}"` : 'sem nome';
  const dims = formatDimensions(
    part.length,
    part.width,
    settings.unit,
    settings.decimalPlaces,
    settings.dimensionOrder,
  );
  const material = part.material.trim() !== '' ? `"${part.material.trim()}"` : 'sem material';

  let message: string;
  if (customMessage !== undefined) {
    message = customMessage;
  } else {
    switch (reason) {
      case 'tooLarge':
        message = `A peça ${name} (${dims}) é maior que qualquer chapa disponível, mesmo girada.`;
        break;
      case 'grainConflict':
        message = `A peça ${name} (${dims}) só caberia girada 90°, mas a direção do veio não permite. Mude o veio para "Indiferente" ou desligue "Considerar veio".`;
        break;
      case 'noMaterialStock':
        message = `Não há chapa de ${material} no estoque para a peça ${name} (${dims}).`;
        break;
      case 'stockExhausted':
        message = `Acabaram as chapas do estoque: ${qty} cópia(s) da peça ${name} (${dims}) ficaram de fora.`;
        break;
      case 'invalid':
        message = `A peça ${name} (${dims}) tem medidas ou quantidade inválidas.`;
        break;
    }
  }

  return {
    partId: part.id,
    label: part.label,
    length: part.length,
    width: part.width,
    material: part.material,
    qty,
    reason,
    message,
  };
}

/**
 * Junta linhas de `unfit` do mesmo par (peca, motivo), somando as quantidades.
 *
 * A chave inclui rotulo, medidas, material e mensagem porque `partId` NAO e
 * garantidamente unico: duas linhas da tabela podem repetir o mesmo id. Juntar
 * so por `partId|reason` faria a linha sobrevivente exibir o rotulo de uma peca
 * com as medidas de outra. Assim, so se funde o que apareceria IDENTICO na tela.
 */
export function mergeUnfit(list: readonly UnfitPart[]): UnfitPart[] {
  const out: UnfitPart[] = [];
  const index = new Map<string, number>();
  for (const u of list) {
    if (u.qty <= 0) continue;
    const key = `${u.partId}|${u.reason}|${u.label}|${u.length}|${u.width}|${u.material}|${u.message}`;
    const at = index.get(key);
    if (at === undefined) {
      index.set(key, out.length);
      out.push({ ...u });
    } else {
      const prev = out[at] as UnfitPart;
      prev.qty += u.qty;
    }
  }
  return out;
}
